import React, { useState, useEffect } from 'react';
import { Tags, X, MapPin, Building2, CircleSlash, CheckCircle2 } from 'lucide-react';

// Marca una base como zona propia de Punto V o cliente externo. Solo afecta
// los reportes del ERP (Resumen): en Punto V no cambia permisos ni cobros.
const OPCIONES = [
  { valor: 'zona_propia', titulo: 'Zona Punto V', detalle: 'Máquinas operadas por Punto V', icono: MapPin },
  { valor: 'cliente', titulo: 'Cliente', detalle: 'Cliente externo que paga mensualidad', icono: Building2 },
  { valor: '', titulo: 'Sin clasificar', detalle: 'Quitar la clasificación', icono: CircleSlash }
];

const DialogoClasificacion = ({ cliente, onConfirmar, onCancel }) => {
  const actual = cliente.clasificacion;
  const [tipo, setTipo] = useState(actual?.tipo || 'zona_propia');
  const [zona, setZona] = useState(actual?.zona || '');
  const [responsable, setResponsable] = useState(actual?.responsable || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const alPresionar = (e) => { if (e.key === 'Escape' && !loading) onCancel(); };
    document.addEventListener('keydown', alPresionar);
    return () => document.removeEventListener('keydown', alPresionar);
  }, [onCancel, loading]);

  const confirmar = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onConfirmar({ tipo: tipo || null, zona: zona.trim() || null, responsable: responsable.trim() || null });
    } catch (err) {
      setError(err.message || 'No se pudo guardar la clasificación. Intente de nuevo.');
      setLoading(false);
    }
  };

  const claseCampo = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none';

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
      onClick={() => !loading && onCancel()}
    >
      <form
        onSubmit={confirmar}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-clasificacion"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="px-6 pt-6 pb-4 flex items-start gap-4">
          <div className="bg-blue-100 text-blue-700 rounded-xl p-2.5 flex-shrink-0">
            <Tags size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="titulo-clasificacion" className="text-lg font-semibold text-gray-900">Clasificar {cliente.slug}</h3>
            <p className="text-sm text-gray-500 truncate">{cliente.duenio.nombre} · {cliente.base_datos}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            aria-label="Cerrar"
            className="text-gray-400 hover:text-gray-600 rounded-lg p-1 focus-visible:ring-2 focus-visible:ring-blue-500 outline-none"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 space-y-2" role="radiogroup" aria-label="Tipo de cuenta">
          {OPCIONES.map(o => (
            <label
              key={o.valor || 'ninguno'}
              className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                tipo === o.valor ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input type="radio" name="tipo" value={o.valor} checked={tipo === o.valor} onChange={() => setTipo(o.valor)} className="mt-1" />
              <o.icono size={16} className="mt-0.5 text-gray-500 flex-shrink-0" />
              <span>
                <span className="block text-sm font-medium text-gray-800">{o.titulo}</span>
                <span className="block text-xs text-gray-500">{o.detalle}</span>
              </span>
            </label>
          ))}
        </div>

        {tipo === 'zona_propia' && (
          <div className="px-6 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Zona <span className="font-normal text-gray-400">(opcional)</span></span>
              <input value={zona} onChange={(e) => setZona(e.target.value)} maxLength={80} placeholder="Ej. Maracay" className={claseCampo} />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Responsable <span className="font-normal text-gray-400">(opcional)</span></span>
              <input value={responsable} onChange={(e) => setResponsable(e.target.value)} maxLength={80} placeholder="Quién opera la zona" className={claseCampo} />
            </label>
          </div>
        )}

        <div className="px-6 pt-4 flex items-start gap-2.5 text-sm text-gray-700">
          <CheckCircle2 size={16} className="mt-0.5 text-green-600 flex-shrink-0" />
          <span>Solo se usa en los reportes del ERP. En Punto V no cambia nada: ni permisos ni cobros.</span>
        </div>

        {error && <div className="mx-6 mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

        <div className="px-6 py-4 mt-5 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
          >
            <Tags size={16} />
            {loading ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DialogoClasificacion;
